/**
 * Customer approval of a test version for production (#3316, epic #3309).
 *
 * Release state comes from staff-api, which reads GitHub (history and the
 * running release) — not from the test database, which the nightly restore
 * replaces.
 */

import { api } from '@/lib/api';

export interface VersionInfo {
  version: string;
  sha: string | null;
  installedAt: string;
}

export interface BuildVersion extends VersionInfo {
  builtAt: string | null;
}

export type ReleaseBlockReason =
  | 'not_configured'
  | 'no_permission'
  | 'unknown_candidate'
  | 'already_in_production'
  | 'github_permission_missing'
  | 'github_unavailable'
  | 'release_in_progress'
  | 'restore_stale';

/** Why GitHub could not be read (#3470). */
export interface GithubErrorSummary {
  kind: 'permission_missing' | 'unavailable';
  /** GitHub's name for the missing permission, e.g. `deployments=read`. */
  permission: string | null;
}

export type ReleaseMode = 'approval' | 'hotfix' | 'rollback';

export interface ReleaseRun {
  approvalId: string | null;
  mode: ReleaseMode | null;
  sha7: string | null;
  status: 'queued' | 'in_progress' | 'success' | 'failure';
  startedAt: string;
  finishedAt: string | null;
  url: string;
}

export interface ReleaseHistoryEntry {
  candidateSha: string;
  mode: ReleaseMode;
  approvedBy: string | null;
  approvedAt: string | null;
  comment: string | null;
  reason: string | null;
  /** Open findings and untested changes the approver acknowledged (#3526); null for older releases. */
  findings: ReleaseFindings | null;
  status: 'pending' | 'in_progress' | 'success' | 'failure';
  createdAt: string;
  updatedAt: string;
}

export interface ReleasesState {
  candidate: BuildVersion;
  production: VersionInfo | null;
  approvers: { userId: string; name: string }[];
  canApprove: boolean;
  blockedReason: ReleaseBlockReason | null;
  latestRun: ReleaseRun | null;
  history: ReleaseHistoryEntry[];
  restore: { restoredAt: string | null; backupStoppedAt: string | null } | null;
  githubUnavailable: boolean;
  githubError: GithubErrorSummary | null;
}

export interface ReleaseNote {
  id: string;
  category: 'new' | 'improved' | 'fixed';
  actionRequired: boolean;
  /** The PR that made the change and the issues it closes (#4197). */
  pr?: number;
  issues?: number[];
  fi: { title: string; body?: string };
  en: { title: string; body?: string };
}

export interface ReleaseChanges {
  status: 'ok' | 'unknown_from' | 'unknown_to' | 'unavailable';
  entries: ReleaseNote[];
  /** GitHub repository `pr` and `issues` link to (#4197); absent from older staff-api builds. */
  repoUrl?: string | null;
}

export function getReleases(): Promise<ReleasesState> {
  return api.get<ReleasesState>('/releases');
}

/** Release notes between production and this build (#3314). */
export function getChangesSinceProduction(): Promise<ReleaseChanges> {
  return api.get<ReleaseChanges>('/version/changes');
}

/** Rows the approval leaves open (#3526); counted by staff-api, recorded with the release. */
export interface ReleaseFindings {
  openNotOk: number;
  untested: number;
  /** Rows marked "not tested" only (#3750); absent from older staff-api builds. */
  notTested?: number;
  total: number;
  resultsIssue: string | null;
}

export interface ApproveReleaseInput {
  sha: string;
  comment?: string;
  /** What the approver saw and acknowledged; staff-api refuses other numbers (409). */
  acknowledged?: { openNotOk: number; untested: number };
}

export function approveRelease(input: ApproveReleaseInput): Promise<{ approvalId: string }> {
  return api.post<{ approvalId: string }>('/releases/approve', input);
}

/**
 * A tester's mark on one change of the test version (#3491): ok, not ok, or
 * not tested because it cannot be tested in the test environment (#3750).
 */
export type TestMarkStatus = 'ok' | 'not_ok' | 'not_tested';

/** A tester's mark on one change of the test version (#3491). */
export interface TestMark {
  sha: string;
  noteId: string;
  status: TestMarkStatus;
  comment: string | null;
  userId: string;
  by: string;
  at: string;
  /**
   * Marked on an earlier candidate that is not yet in production, and carried
   * over because the change's note reads the same (#3547). Absent on older
   * staff-api builds: treat as this version's own mark.
   */
  inherited?: boolean;
  fromSha?: string;
  fromVersion?: string | null;
}

export interface TestResults {
  sha: string;
  configured: boolean;
  /** The GitHub issue that collects this version's marks, once somebody has marked. */
  issueUrl: string | null;
  /** The latest mark of each tester on each change. */
  marks: TestMark[];
  githubError: GithubErrorSummary | null;
}

export function getTestResults(sha: string): Promise<TestResults> {
  return api.get<TestResults>(`/releases/test-results?sha=${encodeURIComponent(sha)}`);
}

export function markTestResult(input: {
  sha: string;
  noteId: string;
  status: TestMarkStatus;
  comment?: string;
}): Promise<{ mark: TestMark; issueUrl: string }> {
  return api.post<{ mark: TestMark; issueUrl: string }>('/releases/test-results', input);
}

export interface PersonalPermissionGrant {
  permission: 'release.approve';
  grantedAt: string;
  grantedByName: string | null;
}

export interface PersonalPermissions {
  data: PersonalPermissionGrant[];
  available: string[];
  /** False in a test environment: grants are made in production. */
  grantable: boolean;
}

export function getPersonalPermissions(userId: string): Promise<PersonalPermissions> {
  return api.get<PersonalPermissions>(`/users/${userId}/personal-permissions`);
}

export function setPersonalPermission(userId: string, permission: string, granted: boolean): Promise<unknown> {
  const url = `/users/${userId}/personal-permissions/${encodeURIComponent(permission)}`;
  return granted ? api.put(url) : api.delete(url);
}
